"use client";

import ImageUpload from "@/components/_shared/controls/ImageUpload";
import Surface from "@/components/_shared/controls/Surface";
import { QueryKeys } from "@/data/globals";
import { CmsItemParameters, CmsSlotType } from "@/data/types";
import { CmsImage } from "@/db/cmsImagesRepository";
import { Button, CircularProgress, Divider, Stack, TextField, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { nanoid } from "nanoid";
import { useEffect, useState } from "react";
import CmsItemParametersEditor from "../slots/CmsItemParametersEditor";

type Props = {
  slotType: CmsSlotType;
  selectedItem: CmsImage;
  inlineEditor?: boolean;
  editingItemParameters?: CmsItemParameters;
  setEditingItemParameters?: (params: CmsItemParameters) => void;
  onSaved?: (item: CmsImage) => void;
  onDeleted?: (itemId: string) => void;
};

const getNewItem = () => ({
  id: nanoid(12),
  key: "",
  url: "",
  validFrom: null,
  validTo: null,
  height: "",
  width: "",
  aspect: "",
  link: ""
});

export default function CmsImageEditorView({
  slotType,
  selectedItem,
  editingItemParameters,
  setEditingItemParameters,
  inlineEditor,
  onSaved,
  onDeleted
}: Props) {
  const queryClient = useQueryClient();
  const [item, setItem] = useState<CmsImage>(getNewItem());
  const [itemParams, setItemParams] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (selectedItem) {
      setItem(selectedItem);
      setItemParams(editingItemParameters);
    } else {
      setItem(getNewItem());
    }
    setDirty(false);
  }, [selectedItem]);

  const saveMutation = useMutation({
    mutationFn: async (data: CmsImage) => {
      const response = await fetch(`/api/admin/cms/items-images/${data.id}`, {
        method: "POST",
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error("Failed to save");
      setEditingItemParameters(itemParams);
      return response.json() as Promise<CmsImage>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CMS_ITEMS_IMAGES] });
      queryClient.setQueryData([QueryKeys.CMS_ITEMS_IMAGES, data.id], data);
      setDirty(false);
      onSaved?.(data);
    }
  });
  const deleteMutation = useMutation({
    mutationFn: async (itemId: string) => {
      const response = await fetch(`/api/admin/cms/items-images/${itemId}`, {
        method: "DELETE"
      });
      if (!response.ok) throw new Error("Failed to delete");
      return response.json();
    },
    onSuccess: (_, itemId) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CMS_ITEMS_IMAGES] });
      setItem(getNewItem());
      setDirty(false);
      onDeleted?.(itemId);
    }
  });

  const updateItem = async () => {
    if (!item.key.trim()) {
      alert("Image key is required");
      return;
    }

    try {
      await saveMutation.mutateAsync(item);
    } catch (error) {
      console.error("Error saving image:", error);
      alert("Failed to save image");
    }
  };

  const deleteItem = async () => {
    if (!item.id) return;
    if (!confirm(`Are you sure you want to delete ${item.key}?`)) return;

    try {
      await deleteMutation.mutateAsync(item.id);
    } catch (error) {
      console.error("Error deleting image:", error);
      alert("Failed to delete image");
    }
  };

  const saving = saveMutation.isPending || deleteMutation.isPending;

  return (
    <Surface>
      <Stack direction="row" sx={{ p: 2 }} spacing={2}>
        <TextField
          size="small"
          label="Image Key"
          value={item.key}
          onChange={(e) => {
            setItem((x) => ({ ...x, key: e.target.value }));
            setDirty(true);
          }}
          sx={{ flex: 1 }}
          disabled={saving || inlineEditor}
        />
        {saving ? (
          <CircularProgress size={32} sx={{ alignSelf: "center" }} />
        ) : (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={() => updateItem()}
              disabled={!dirty || !item.url}
              sx={{ ml: 2 }}
            >
              Save
            </Button>
            {item.key && (
              <Button variant="outlined" color="error" onClick={() => deleteItem()}>
                Delete
              </Button>
            )}
          </Stack>
        )}
      </Stack>
      <Divider />
      <Stack sx={{ p: 2 }} spacing={3}>
        <ImageUpload
          value={item.url}
          onUpload={(url) => {
            setItem((x) => ({ ...x, url }));
            setDirty(true);
          }}
          onRemove={() => {
            setItem((x) => ({ ...x, url: "" }));
            setDirty(true);
          }}
          assetType="cms"
          label="Upload CMS Image"
          disabled={saving}
        />

        <TextField
          label="Link"
          size="small"
          value={item.link || ""}
          onChange={(e) => {
            setItem((x) => ({ ...x, link: e.target.value }));
            setDirty(true);
          }}
          placeholder="https://army-forge.onepagerules.com"
          disabled={saving}
          fullWidth
        />

        <div>
          <Typography variant="label" sx={{ mb: 1 }}>
            Validity
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Valid From"
              type="datetime-local"
              size="small"
              value={item.validFrom || ""}
              onChange={(e) => {
                setItem((x) => ({ ...x, validFrom: new Date(e.target.value) }));
                setDirty(true);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
              disabled={saving}
              fullWidth
            />

            <TextField
              label="Valid To"
              type="datetime-local"
              size="small"
              value={item.validTo || ""}
              onChange={(e) => {
                setItem((x) => ({ ...x, validTo: new Date(e.target.value) }));
                setDirty(true);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
              disabled={saving}
              fullWidth
            />
          </Stack>
        </div>

        <div>
          <Typography variant="label" sx={{ mb: 1 }}>
            Advanced Options
          </Typography>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Width"
              size="small"
              value={item.width || ""}
              onChange={(e) => {
                setItem((x) => ({ ...x, width: e.target.value }));
                setDirty(true);
              }}
              helperText="Optional. e.g. 400px, 50%, 2rem"
              disabled={saving}
              fullWidth
            />
            <TextField
              label="Height"
              size="small"
              value={item.height || ""}
              onChange={(e) => {
                setItem((x) => ({ ...x, height: e.target.value }));
                setDirty(true);
              }}
              helperText="Optional. e.g. 200px"
              disabled={saving}
              fullWidth
            />
            <TextField
              label="Aspect Ratio"
              size="small"
              value={item.aspect || ""}
              onChange={(e) => {
                setItem((x) => ({ ...x, aspect: e.target.value }));
                setDirty(true);
              }}
              helperText="Optional. e.g. 16/9."
              disabled={saving}
              fullWidth
            />
          </Stack>
        </div>
      </Stack>
      {slotType !== null && (
        <CmsItemParametersEditor
          type={slotType}
          parameters={itemParams ?? {}}
          setItemParameters={(x) => {
            console.log("CmsItemParametersEditor setItemParameters", x);
            //if (!editingContent) return;
            setDirty(true);
            setItemParams(x);
            //setContentItem({ ...editingContent, parameters: x });
          }}
        />
      )}
    </Surface>
  );
}
